import React from 'react';
import Header from '../components/header';
import Footer from '../components/footer';
import FloatingChatButton from '../components/FloatingChatButton';
import AllHospitalsCarousel from '../components/AllHospitalsCarousel';
import AllDoctorsCarousel from '../components/AllDoctorsCarousel';
import FeaturedDoctorsCarousel from '../components/FeaturedDoctorsCarousel';
import BlogsCarousel from '../components/BlogsCarousel';
import TestimonialVideoCarousel from '../components/TestimonialVideoCarousel';
import FeaturedTestimonialVideoCarousel from '../components/FeaturedTestimonialVideoCarousel';
import TestimonialsCarousel from '../components/TestimonialsCarousel';
import NewsCarousel from '../components/NewsCarousel';
import CostEstimateForm from '../components/CostEstimateForm';
import CallbackPopup from '../components/CallbackPopup';
import { useBestDoctors } from '../hooks/useDoctors';
import { Link, useNavigate } from 'react-router-dom';
import { hospitalNameToSlug } from '../utils/hospitalUtils';
import { treatmentTitleToSlug } from '../utils/treatmentUtils';
import { useCarousel } from '../hooks/useCarousel';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/free-mode';
import 'swiper/css/pagination';
import SwiperCore from 'swiper';
import { FreeMode, Autoplay, Navigation } from 'swiper/modules';
import { doctorService, hospitalService, treatmentService, newsService } from '../services/api';

SwiperCore.use([FreeMode, Autoplay, Navigation]);

// Debounce utility function
const debounce = (func, wait) => {
  let timeout;
  const debounced = (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(this, args), wait);
  };
  debounced.cancel = () => clearTimeout(timeout);
  return debounced;
};

const Home = () => {
  const navigate = useNavigate();

  // Search functionality
  const [searchQuery, setSearchQuery] = React.useState('');
  const [searchResults, setSearchResults] = React.useState({ doctors: [], hospitals: [], treatments: [] });
  const [isSearching, setIsSearching] = React.useState(false);
  const [showResults, setShowResults] = React.useState(false);
  const searchRef = React.useRef(null);

  // Callback popup state
  const [isCallbackPopupOpen, setIsCallbackPopupOpen] = React.useState(false);

  // Search ranking utility functions
  const calculateRelevanceScore = (item, query, type) => {
    const lowerQuery = query.toLowerCase().trim();
    let score = 0;
    
    // Primary fields for ranking (starting character matches get highest priority)
    let primaryField = '';
    if (type === 'doctor') {
      primaryField = item.name?.toLowerCase() || '';
    } else if (type === 'hospital') {
      primaryField = item.name?.toLowerCase() || '';
    } else if (type === 'treatment') {
      primaryField = item.title?.toLowerCase() || '';
    }
    
    // Highest priority: Exact match at start of primary field
    if (primaryField.startsWith(lowerQuery)) {
      score += 1000;
    }
    // High priority: Primary field contains query at word boundary
    else if (primaryField.includes(' ' + lowerQuery) || primaryField.includes('-' + lowerQuery)) {
      score += 800;
    }
    // Medium-high priority: Primary field contains query anywhere
    else if (primaryField.includes(lowerQuery)) {
      score += 600;
    }
    
    // Secondary fields for additional scoring
    const secondaryFields = [];
    if (type === 'doctor') {
      secondaryFields.push(
        item.specialty?.toLowerCase() || '',
        item.hospital?.toLowerCase() || '',
        item.description?.toLowerCase() || ''
      );
    } else if (type === 'hospital') {
      secondaryFields.push(
        item.country?.toLowerCase() || '',
        ...(item.specialities?.map(s => s.toLowerCase()) || []),
        item.description?.toLowerCase() || ''
      );
    } else if (type === 'treatment') {
      secondaryFields.push(
        ...(item.associatedSpecialities?.map(s => s.toLowerCase()) || []),
        item.description?.toLowerCase() || '',
        item.shortDescription?.toLowerCase() || ''
      );
    }
    
    // Check secondary fields for matches
    secondaryFields.forEach(field => {
      if (field.startsWith(lowerQuery)) {
        score += 400;
      } else if (field.includes(' ' + lowerQuery) || field.includes('-' + lowerQuery)) {
        score += 200;
      } else if (field.includes(lowerQuery)) {
        score += 100;
      }
    });
    
    // Bonus for shorter field names (more specific matches)
    if (primaryField.length > 0 && primaryField.includes(lowerQuery)) {
      score += Math.max(0, 100 - primaryField.length);
    }
    
    return score;
  };

  const rankSearchResults = (results, query) => {
    const rankedResults = {
      doctors: [...results.doctors],
      hospitals: [...results.hospitals],
      treatments: [...results.treatments]
    };
    
    // Rank each category
    rankedResults.doctors = rankedResults.doctors
      .map(doctor => ({
        ...doctor,
        relevanceScore: calculateRelevanceScore(doctor, query, 'doctor')
      }))
      .sort((a, b) => b.relevanceScore - a.relevanceScore);
    
    rankedResults.hospitals = rankedResults.hospitals
      .map(hospital => ({
        ...hospital,
        relevanceScore: calculateRelevanceScore(hospital, query, 'hospital')
      }))
      .sort((a, b) => b.relevanceScore - a.relevanceScore);
    
    rankedResults.treatments = rankedResults.treatments
      .map(treatment => ({
        ...treatment,
        relevanceScore: calculateRelevanceScore(treatment, query, 'treatment')
      }))
      .sort((a, b) => b.relevanceScore - a.relevanceScore);
    
    return rankedResults;
  };

  // Debounced search function
  const performSearch = React.useCallback(
    debounce(async (query) => {
      if (query.trim().length < 2) {
        setSearchResults({ doctors: [], hospitals: [], treatments: [] });
        setShowResults(false);
        return;
      }

      setIsSearching(true);
      try {
        // Search doctors, hospitals, and treatments in parallel with timeout handling
        // Increase limits to get more results for better ranking
        const searchPromises = [
          doctorService.getAll({ search: query, limit: 20 }).catch(err => {
            console.warn('Doctor search failed:', err.message);
            return { data: { doctors: [] } };
          }),
          hospitalService.getAll({ search: query, limit: 20 }).catch(err => {
            console.warn('Hospital search failed:', err.message);
            return { data: { hospitals: [] } };
          }),
          treatmentService.getAll({ search: query, limit: 20, status: 'published' }).catch(err => {
            console.warn('Treatment search failed:', err.message);
            return { data: { treatments: [] } };
          })
        ];

        const [doctorsResponse, hospitalsResponse, treatmentsResponse] = await Promise.all(searchPromises);

        const rawResults = {
          doctors: doctorsResponse.data?.doctors || [],
          hospitals: hospitalsResponse.data?.hospitals || [],
          treatments: treatmentsResponse.data?.treatments || []
        };

        // Apply ranking system
        const rankedResults = rankSearchResults(rawResults, query);
        
        setSearchResults(rankedResults);
        setShowResults(true);
      } catch (error) {
        console.error('Search error:', error);
        setSearchResults({ doctors: [], hospitals: [], treatments: [] });
        setShowResults(false);
      } finally {
        setIsSearching(false);
      }
    }, 300),
    []
  );

  // Function to get ordered sections based on highest relevance scores
  const getOrderedSections = (searchResults) => {
    const sections = [];
    
    // Calculate max score for each category
    const maxScores = {
      doctors: Math.max(...searchResults.doctors.map(d => d.relevanceScore || 0), 0),
      hospitals: Math.max(...searchResults.hospitals.map(h => h.relevanceScore || 0), 0),
      treatments: Math.max(...searchResults.treatments.map(t => t.relevanceScore || 0), 0)
    };
    
    // Create section objects with their max scores
    if (searchResults.doctors.length > 0) {
      sections.push({ type: 'doctors', maxScore: maxScores.doctors, data: searchResults.doctors });
    }
    if (searchResults.hospitals.length > 0) {
      sections.push({ type: 'hospitals', maxScore: maxScores.hospitals, data: searchResults.hospitals });
    }
    if (searchResults.treatments.length > 0) {
      sections.push({ type: 'treatments', maxScore: maxScores.treatments, data: searchResults.treatments });
    }
    
    // Sort by highest max score first
    return sections.sort((a, b) => b.maxScore - a.maxScore);
  };

  // Function to render a search result section
  const renderSearchSection = (section) => {
    const { type, data } = section;
    
    if (type === 'doctors') {
      return (
        <div key="doctors" className="p-2">
          <h3 className="text-sm font-semibold text-gray-600 px-3 py-2 border-b border-gray-100 font-montserrat">
            Doctors
            {data[0]?.relevanceScore >= 1000 && (
              <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-1 rounded">Best Match</span>
            )}
          </h3>
          {data.map((doctor) => (
            <div
              key={`doctor-${doctor.id}`}
              onClick={() => handleResultClick('doctor', doctor)}
              className="flex items-center p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-50 last:border-b-0"
            >
              <img
                src={doctor.image || '/images/default-doctor.jpg'}
                alt={doctor.name}
                className="w-12 h-12 rounded-full object-cover mr-3"
              />
              <div className="flex-1">
                <h4 className="font-semibold text-gray-900 font-montserrat">{doctor.name}</h4>
                <p className="text-sm text-gray-600 font-open-sans">{doctor.specialty}</p>
                <p className="text-xs text-blue-600 font-open-sans">{doctor.hospital}</p>
              </div>
            </div>
          ))}
        </div>
      );
    }
    
    if (type === 'hospitals') {
      return (
        <div key="hospitals" className="p-2">
          <h3 className="text-sm font-semibold text-gray-600 px-3 py-2 border-b border-gray-100 font-montserrat">
            Hospitals
            {data[0]?.relevanceScore >= 1000 && (
              <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-1 rounded">Best Match</span>
            )}
          </h3>
          {data.map((hospital) => (
            <div
              key={`hospital-${hospital.id}`}
              onClick={() => handleResultClick('hospital', hospital)}
              className="flex items-center p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-50 last:border-b-0"
            >
              <img
                src={hospital.mainImage || '/images/default-hospital.jpg'}
                alt={hospital.name}
                className="w-12 h-12 rounded-lg object-cover mr-3"
              />
              <div className="flex-1">
                <h4 className="font-semibold text-gray-900 font-montserrat">{hospital.name}</h4>
                <p className="text-sm text-gray-600 font-open-sans">{hospital.country}</p>
                <p className="text-xs text-blue-600 font-open-sans">{hospital.specialities?.join(', ')}</p>
              </div>
            </div>
          ))}
        </div>
      );
    }
    
    if (type === 'treatments') {
      return (
        <div key="treatments" className="p-2">
          <h3 className="text-sm font-semibold text-gray-600 px-3 py-2 border-b border-gray-100 font-montserrat">
            Treatments
            {data[0]?.relevanceScore >= 1000 && (
              <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-1 rounded">Best Match</span>
            )}
          </h3>
          {data.map((treatment) => (
            <div
              key={`treatment-${treatment.id || treatment._id}`}
              onClick={() => handleResultClick('treatment', treatment)}
              className="flex items-center p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-50 last:border-b-0"
            >
              <img
                src={treatment.headerImage || '/images/default-treatment.jpg'}
                alt={treatment.title}
                className="w-12 h-12 rounded-lg object-cover mr-3"
              />
              <div className="flex-1">
                <h4 className="font-semibold text-gray-900 font-montserrat">{treatment.title}</h4>
                <p className="text-sm text-gray-600 font-open-sans">{treatment.associatedSpecialities?.slice(0, 2)?.join(', ')}</p>
                <p className="text-xs text-blue-600 font-open-sans">
                  {treatment.costInfo?.india ? `Starting from $${treatment.costInfo.india.minCost}` : 'Cost info available'}
                </p>
              </div>
            </div>
          ))}
        </div>
      );
    }
    
    return null;
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    performSearch(query);
  };

  // Handle clicking on a search result
  const handleResultClick = (type, item) => {
    setShowResults(false);
    setSearchQuery('');
    
    if (type === 'doctor') {
      navigate(`/doctor-details/${item.id}`);
    } else if (type === 'hospital') {
      navigate(`/hospital/${hospitalNameToSlug(item.name)}`);
    } else if (type === 'treatment') {
      navigate(`/treatments/${item.slug || treatmentTitleToSlug(item.title)}`);
    }
  };

  // Handle search button click
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      // Navigate to a more comprehensive search results page
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      setShowResults(false);
      setSearchQuery('');
    }
  };

  // Close search results when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Cleanup debounced function
  React.useEffect(() => {
    return () => {
      performSearch.cancel();
    };
  }, [performSearch]);

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="flex-grow">
        {/* Hero Section */}
        <section className="container mx-auto px-4 py-10 md:py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div>
              <h1 className="text-4xl md:text-5xl font-bold mb-6 font-montserrat">
                <span className="block text-gray-900">Get</span>
                <span className="block text-[#1976f2]">Medical Treatment</span>
                <span className="block text-gray-900">From Best Doctors Of The World</span>
              </h1>
              
              {/* Search Box */}
              <div className="relative mt-8 mb-10" ref={searchRef}>
                <form onSubmit={handleSearchSubmit}>
                  <input 
                    type="text" 
                    value={searchQuery}
                    onChange={handleSearchChange}
                    placeholder="Search for Hospitals/Doctors/Specialisation"
                    className="w-full py-3 px-4 rounded-full border border-gray-300 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-open-sans"
                  />
                  <button 
                    type="submit"
                    className="absolute right-1 top-1/2 transform -translate-y-1/2 bg-[#1976f2] text-white p-2 rounded-full hover:bg-blue-600 transition-colors"
                  >
                    {isSearching ? (
                      <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></div>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    )}
                  </button>
                </form>

                {/* Search Results Dropdown */}
                {showResults && (searchResults.doctors.length > 0 || searchResults.hospitals.length > 0 || searchResults.treatments.length > 0) && (
                  <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-lg mt-1 z-50 max-h-96 overflow-y-auto">
                    {/* Render sections in order of relevance */}
                    {getOrderedSections(searchResults).map(section => renderSearchSection(section))}

                    {/* View All Results */}
                    <div className="p-3 border-t border-gray-100">
                      <button
                        onClick={handleSearchSubmit}
                        className="w-full text-center text-blue-600 hover:text-blue-800 font-medium font-montserrat"
                      >
                        View all results for "{searchQuery}"
                      </button>
                    </div>
                  </div>
                )}

                {/* No Results Message */}
                {showResults && searchResults.doctors.length === 0 && searchResults.hospitals.length === 0 && searchResults.treatments.length === 0 && !isSearching && searchQuery.length >= 2 && (
                  <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-lg mt-1 z-50">
                    <div className="p-4 text-center text-gray-500 font-open-sans">
                      No results found for "{searchQuery}"
                    </div>
                  </div>
                )}
              </div>
              
              {/* Statistics */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="flex items-center mb-4">
                    <span className="text-[#1976f2] text-xl font-bold mr-2 font-montserrat">250+</span>
                    <span className="font-open-sans">Hospitals</span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-[#1976f2] text-xl font-bold mr-2 font-montserrat">250+</span>
                    <span className="font-open-sans">Treatments</span>
                  </div>
                </div>
                <div>
                  <div className="flex items-center mb-4">
                    <span className="text-[#1976f2] text-xl font-bold mr-2 font-montserrat">500+</span>
                    <span className="font-open-sans">Doctors</span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-[#1976f2] text-xl font-bold mr-2 font-montserrat">25,000+</span>
                    <span className="font-open-sans">Patients</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div>
              <div className="rounded-lg overflow-hidden shadow-lg border border-gray-100">
                <img 
                  src="/images/hero-banner-img.jpg" 
                  alt="Doctor consulting with patient" 
                  className="w-full h-auto object-cover"
                />
              </div>
            </div>
          </div>
        </section>
        
        {/* Cost Estimate Section */}
        <section className="container mx-auto px-4 py-16">
          <div className="bg-white rounded-lg shadow-md p-8 flex flex-col md:flex-row gap-16 justify-between items-center min-h-[400px]">
            {/* Left: Form */}
            <div className="md:w-1/2 w-full flex flex-col justify-center h-full">
              <h2 className="text-2xl font-bold mb-4 text-left font-montserrat">Get Your Cost Estimate Now!</h2>
              <CostEstimateForm />
            </div>
            {/* Right: Call to Action */}
            <div className="md:w-1/2 w-full flex flex-col justify-center items-center md:items-start text-center md:text-left h-full pt-6 md:pt-12 px-4 md:pl-32">
  <h3 className="text-2xl md:text-4xl font-bold mb-4 font-montserrat">
    Start your <span className="text-[#1976f2]">healing journey today</span>
  </h3>
  <div className="mt-6 md:mt-10 mb-6 w-full">
    <span className="block text-lg md:text-2xl mb-4 md:mb-6 font-open-sans">Take an Expert Second Opinion</span>
    <div className="flex flex-col sm:flex-row gap-3 md:gap-6 justify-center md:justify-start">
      <button 
        onClick={() => setIsCallbackPopupOpen(true)}
        className="bg-[#1976f2] text-white px-6 md:px-8 py-3 rounded-full font-semibold hover:bg-blue-600 transition font-montserrat text-sm md:text-base w-full sm:w-auto min-w-[140px]"
      >
        Get A Call Back
      </button>
      <button 
        onClick={() => window.open('https://api.whatsapp.com/send/?phone=9599222655&text=Hello%2C+please+contact+me+regarding+https%3A%2F%2Fwww.medicallinkers.org%2Fhospitals%2F+%2C+Thank+you%21&type=phone_number&app_absent=0', '_blank')}
        className="border border-[#1976f2] text-[#1976f2] px-6 md:px-8 py-3 rounded-full font-semibold hover:bg-blue-50 transition font-montserrat text-sm md:text-base w-full sm:w-auto min-w-[140px] flex items-center justify-center gap-2"
      >
        <i className="ri-whatsapp-line"></i> Chat Now
      </button>
    </div>
  </div>
</div>
          </div>
        </section>
        
        {/* World-Class Treatment Plans Section */}
        <section className="bg-white py-16">
          <div className="container mx-auto px-4">
            <h2 className="text-4xl font-bold text-center mb-12 font-montserrat">
              <span className="text-black">World-Class </span><span className="text-[#1976f2]">Treatment Plans</span>
            </h2>
            
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-center">
              
              {/* Left: Treatment Grid */}
              <div className="lg:col-span-2">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {[
                    { icon: '/images/image copy 56.png', name: 'Lung Transplant', price: '$50000', slug: 'lung-transplant' },
                    { icon: '/images/image copy 55.png', name: 'Liver Transplant', price: '$25000', slug: 'liver-transplant' },
                    { icon: '/images/image copy 52.png', name: 'Kidney Transplant', price: '$11000', slug: 'kidney-transplant' },
                    { icon: '/images/image copy 57.png', name: 'Lung Cancer Treatment', price: '$5500', slug: 'lung-cancer-treatment' },
                    { icon: '/images/image copy 58.png', name: 'Cervical Disc Replacement', price: '$6500', slug: 'cervical-disc-replacement' },
                    { icon: '/images/image copy 59.png', name: 'Deep Brain Stimulation Surgery', price: '$20000', slug: 'deep-brain-stimulation-surgery' },
                    { icon: '/images/image copy 57.png', name: 'Bone Marrow Transplant', price: '$25000', slug: 'bone-marrow-transplant' },
                    { icon: '/images/image copy 60.png', name: 'Heart Transplant', price: '$45000', slug: 'heart-transplant' },
                  ].map((plan) => (
                    <div key={plan.name} className="flex items-start gap-4 p-3 rounded-lg hover:bg-gray-50 transition-colors">
                      <div className="flex-shrink-0">
                        <img 
                          src={plan.icon} 
                          alt={plan.name} 
                          className="w-16 h-16 object-contain" 
                        />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900 text-xl mb-2 font-montserrat">{plan.name}</h3>
                        <p className="text-gray-600 text-base mb-3 font-open-sans">Starting from {plan.price}</p>
                        <button 
                          onClick={() => {
                            navigate(`/treatments/${plan.slug}`);
                            window.scrollTo(0, 0);
                          }}
                          className="text-[#1976f2] text-base font-medium hover:underline inline-flex items-center font-open-sans cursor-pointer"
                        >
                          Explore More
                          <svg className="w-5 h-5 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Right: Doctor Image */}
              <div className="hidden lg:flex justify-center items-center">
                <div className="relative">
                  <img 
                    src="/images/image copy 54.png" 
                    alt="Professional Doctor" 
                    className="h-[550px] w-auto object-cover rounded-lg shadow-lg" 
                  />
                </div>
              </div>

            </div>
          </div>
        </section>
        
        {/* Treatment Specialities Section */}
        <section className="container mx-auto px-4 py-16">
  <h2 className="text-4xl font-bold text-center mb-12 font-montserrat">
    <span className="text-black">Treatment </span><span className="text-[#1976f2]">Specialities</span>
  </h2>
  
  {/* Outer div to center the entire block on the page */}
  <div className="flex justify-center">
    
    {/* This inner div handles the side-by-side layout of the image block and the "Discover" card */}
    <div className="flex flex-col lg:flex-row items-stretch gap-x-6 gap-y-8">
      
      {/* Container for the two rows of speciality images */}
      <div className="flex flex-col items-center gap-y-6">
        
        {/* TOP ROW: 6 items, centered */}
        <div className="flex justify-center flex-wrap gap-4">
          {[
            { name: 'Cardiology', imgSrc: '/images/image copy 43.png' },
            { name: 'Cosmetics', imgSrc: '/images/image copy 44.png' },
            { name: 'Gynaecology', imgSrc: '/images/image copy 45.png' },
            { name: 'Hematology', imgSrc: '/images/image copy 46.png' },
            { name: 'Fertility', imgSrc: '/images/image copy 47.png' },
            { name: 'Oncology', imgSrc: '/images/image copy 48.png' },
          ].map((speciality) => (
            <a href={`/speciality/${speciality.name.toLowerCase().replace(/\s+/g, "-")}`} key={speciality.name} className="transition-transform duration-300 hover:scale-105">
              <img 
                src={speciality.imgSrc} 
                alt={speciality.name} 
                className="w-36 h-auto object-contain rounded-xl shadow-md"
              />
            </a>
          ))}
        </div>
        
        {/* BOTTOM ROW: 5 items, centered to create the offset */}
        <div className="flex justify-center flex-wrap gap-4">
          {[
            { name: 'Orthopedics', imgSrc: '/images/focussedortho.png' },
            { name: 'Neurosurgery', imgSrc: '/images/image copy 50.png' },
            { name: 'Liver', imgSrc: '/images/image copy 51.png' },
            { name: 'Kidney', imgSrc: '/images/image copy 52.png' },
            { name: 'Weight Loss', imgSrc: '/images/image copy 53.png' },
          ].map((speciality) => (
            <a href={`/speciality/${speciality.name.toLowerCase().replace(/\s+/g, "-")}`} key={speciality.name} className="transition-transform duration-300 hover:scale-105">
              <img 
                src={speciality.imgSrc} 
                alt={speciality.name} 
                className="w-36 h-auto object-contain rounded-xl shadow-md"
              />
            </a>
          ))}
        </div>
        
      </div>

      {/* "Discover all" Card */}
      <div className="bg-[#1976f2] text-white rounded-xl flex flex-col items-center justify-center p-6 text-center shadow-lg w-full lg:w-44 transition-transform duration-300 hover:scale-105">
        <span className="font-semibold text-lg leading-tight font-montserrat">Discover all Specialities & Treatments</span>
        <a href="/speciality/cardiology" className="mt-4 flex items-center justify-center w-12 h-12 rounded-full bg-white hover:bg-blue-100 transition">
           <svg className="w-7 h-7 text-[#1976f2]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
             <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5-5 5M6 12h11" />
           </svg>
         </a>
      </div>

    </div>
  </div>
</section>
        
        {/* Network Of Hospitals Section */}
        <AllHospitalsCarousel />
        
        {/* Featured Doctors Section */}
        <FeaturedDoctorsCarousel />
        
        {/* Countries Available Section */}
        <section className="container mx-auto px-4 py-16">
          <h2 className="text-4xl font-bold text-center font-montserrat">
            <span className="text-[#1976f2]">Countries</span> Available
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mt-12">
            <div className="flex flex-col items-center">
              <img 
                src="/images/image.png" 
                alt="India" 
                className="rounded-lg w-full h-48 object-cover mb-4"
              />
              <h3 className="text-2xl font-bold mb-2 font-montserrat">India</h3>
              <a href="/IndiaMedicalPage" className="text-[#1976f2] flex items-center font-open-sans">
                Explore more 
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </a>
            </div>
            <div className="flex flex-col items-center">
              <img 
                src="/images/image copy.png" 
                alt="Turkey" 
                className="rounded-lg w-full h-48 object-cover mb-4"
              />
              <h3 className="text-2xl font-bold mb-2 font-montserrat">Turkey</h3>
              <a href="/turkeymedicalpage" className="text-[#1976f2] flex items-center font-open-sans">
                Explore more 
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </a>
            </div>
            <div className="flex flex-col items-center">
              <img 
                src="/images/image copy 8.png" 
                alt="Thailand" 
                className="rounded-lg w-full h-48 object-cover mb-4"
              />
              <h3 className="text-2xl font-bold mb-2 font-montserrat">Thailand</h3>
              <a href="/thailandmedicalpage" className="text-[#1976f2] flex items-center font-open-sans">
                Explore more 
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </a>
            </div>
            <div className="flex flex-col items-center">
              <img 
                src="/images/image copy 9.png" 
                alt="South Korea" 
                className="rounded-lg w-full h-48 object-cover mb-4"
              />
              <h3 className="text-2xl font-bold mb-2 font-montserrat">South Korea</h3>
              <a href="/southkoreamedicalpage" className="text-[#1976f2] flex items-center font-open-sans">
                Explore more 
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </a>
            </div>
          </div>
        </section>

        {/* Real Stories, Real Impact Section - Dynamic Carousel */}
        <TestimonialsCarousel title="Real Stories, Real Impact"  />

        {/* What makes us different Section */}
        <section className="container mx-auto px-4 py-16 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-[#1976f2] mb-8 font-montserrat">What makes us different?</h2>
          <div className="flex justify-center mb-6">
            <img src="/images/image copy 61.png" alt="Personal Care" className="rounded-xl w-full max-w-7xl h-72 object-cover" />
          </div>
          <div className="text-xl font-semibold mb-2 font-montserrat">Personal Care Assurety</div>
          <div className="text-gray-700 max-w-3xl mx-auto leading-relaxed font-open-sans">
            <p className="mb-2">We guarantee patient care support throughout your treatment journey.</p>
            <p>Our dedicated care executive stands besides the patient through every step, ensuring a hassle free & effective treatment.</p>
          </div>
        </section>

        {/* Dynamic Blogs Section */}
        <BlogsCarousel title="Recent Blogs"  />

        {/* Testimonial Videos Section */}
        

        {/* Featured Testimonial Videos Section */}
        <FeaturedTestimonialVideoCarousel 
          title="Featured Patient Success Stories"
          subtitle="Our most inspiring patient testimonials and recovery journeys"
        />

        {/* Plan Your Treatment Today Section */}
        <section className="w-full bg-[#eaf6ff] py-16">
          <div className="container mx-auto px-4">
            <h2 className="text-4xl font-bold text-center mb-12 font-montserrat">
              <span className="text-[#1976f2]">Plan Your</span> Treatment Today
            </h2>
            <PlanStats onOpenCallbackPopup={() => setIsCallbackPopupOpen(true)} />
          </div>
        </section>

        {/* News & Events Section */}
        <NewsCarousel title="News & Events" limit={8} />
      </main>
      
      <Footer />

      {/* Floating Chat Button */}
      <FloatingChatButton />

      {/* Callback Popup */}
      <CallbackPopup 
        isOpen={isCallbackPopupOpen} 
        onClose={() => setIsCallbackPopupOpen(false)} 
      />
    </div>
  );
};

function BestDoctorsSlider() {
  const { doctors: bestDoctors, loading } = useBestDoctors();
  const { currentIndex, goToNext, goToPrev, canGoNext, canGoPrev } = useCarousel(
    bestDoctors, 
    4, 
    5000
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-xl text-blue-600">Loading doctors...</div>
      </div>
    );
  }

  if (!bestDoctors.length) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-xl text-gray-600">No doctors available</div>
      </div>
    );
  }

  return (
    <>
      <div className="relative overflow-hidden">
        <div 
          className="flex transition-transform duration-500 ease-in-out gap-6"
          style={{
            transform: `translateX(-${currentIndex * (100 / bestDoctors.length)}%)`,
            width: `${bestDoctors.length * (100 / 4)}%`
          }}
        >
          {bestDoctors.map((doctor) => (
            <div 
              key={doctor.id} 
              className="bg-[#f7fafd] rounded-xl flex flex-col items-center p-6 shadow-md flex-shrink-0"
              style={{ width: `${100 / bestDoctors.length}%` }}
            >
              <img 
                src={doctor.image} 
                alt={doctor.name} 
                className="w-40 h-40 object-contain rounded-xl mb-4 bg-white"
              />
              <Link 
                to={`/doctor-details/${doctor.id}`}
                className="text-lg font-semibold text-center mb-1 text-blue-600 hover:text-blue-800 hover:underline font-montserrat"
              >
                {doctor.name}
              </Link>
              <div className="text-sm text-gray-700 text-center mb-1 font-open-sans">{doctor.specialty} | {doctor.experience}</div>
              <Link 
                to={`/hospital/${hospitalNameToSlug(doctor.hospital)}`}
                className="text-sm font-semibold text-center text-blue-600 hover:text-blue-800 hover:underline font-open-sans"
              >
                {doctor.hospital}
              </Link>
            </div>
          ))}
        </div>
      </div>
      
      <div className="flex justify-center space-x-4 mt-8">
        <button 
          onClick={goToPrev} 
          disabled={!canGoPrev}
          className={`w-12 h-12 rounded-full flex items-center justify-center ${
            !canGoPrev ? 'bg-gray-300 cursor-not-allowed' : 'bg-[#1976f2] hover:bg-blue-600'
          } text-white transition-colors`}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <button 
          onClick={goToNext} 
          disabled={!canGoNext}
          className={`w-12 h-12 rounded-full flex items-center justify-center ${
            !canGoNext ? 'bg-gray-300 cursor-not-allowed' : 'bg-[#1976f2] hover:bg-blue-600'
          } text-white transition-colors`}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
    </>
  );
}

function useCountUp(target, duration = 1200) {
  const [count, setCount] = React.useState(0);
  React.useEffect(() => {
    let start = 0;
    const increment = Math.ceil(target / (duration / 16));
    const interval = setInterval(() => {
      start += increment;
      if (start >= target) {
        setCount(target);
        clearInterval(interval);
      } else {
        setCount(start);
      }
    }, 16);
    return () => clearInterval(interval);
  }, [target, duration]);
  return count.toLocaleString();
}

function useCountUpOnView(target, duration = 1200, trigger) {
  const [count, setCount] = React.useState(0);
  React.useEffect(() => {
    if (!trigger) return;
    setCount(0);
    let start = 0;
    const increment = Math.ceil(target / (duration / 16));
    const interval = setInterval(() => {
      start += increment;
      if (start >= target) {
        setCount(target);
        clearInterval(interval);
      } else {
        setCount(start);
      }
    }, 16);
    return () => clearInterval(interval);
  }, [target, duration, trigger]);
  return count.toLocaleString();
}

function PlanStats({ onOpenCallbackPopup }) {
  const navigate = useNavigate();
  const sectionRef = React.useRef(null);
  const [inView, setInView] = React.useState(false);

  React.useEffect(() => {
    const observer = new window.IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { threshold: 0.5 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const hospitals = useCountUpOnView(250, 1200, inView);
  const doctors = useCountUpOnView(500, 1200, inView);
  const treatments = useCountUpOnView(250, 1200, inView);
  const patients = useCountUpOnView(25000, 1200, inView);

  return (
    <div ref={sectionRef}>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-10">
        <div className="flex flex-col items-center">
          <div className="bg-white rounded-lg p-4 mb-4">
            <img src="/images/image copy 65.png" alt="Hospitals" className="w-12 h-12 object-contain" />
          </div>
          <div className="text-3xl font-bold font-montserrat">{hospitals}</div>
          <div className="text-lg text-gray-700 font-open-sans">Hospitals</div>
        </div>
        <div className="flex flex-col items-center">
          <div className="bg-white rounded-lg p-4 mb-4">
            <img src="/images/image copy 66.png" alt="Doctors" className="w-12 h-12 object-contain" />
          </div>
          <div className="text-3xl font-bold font-montserrat">{doctors}</div>
          <div className="text-lg text-gray-700 font-open-sans">Doctors</div>
        </div>
        <div className="flex flex-col items-center">
          <div className="bg-white rounded-lg p-4 mb-4">
            <img src="/images/image copy 67.png" alt="Treatments" className="w-12 h-12 object-contain" />
          </div>
          <div className="text-3xl font-bold font-montserrat">{treatments}</div>
          <div className="text-lg text-gray-700 font-open-sans">Treatments</div>
        </div>
        <div className="flex flex-col items-center">
          <div className="bg-white rounded-lg p-4 mb-4">
            <img src="/images/image copy 68.png" alt="Patients" className="w-12 h-12 object-contain" />
          </div>
          <div className="text-3xl font-bold font-montserrat">{patients}</div>
          <div className="text-lg text-gray-700 font-open-sans">Patients</div>
        </div>
      </div>
      <div className="flex justify-center gap-4 mt-4">
        <button 
          onClick={() => onOpenCallbackPopup()}
          className="bg-[#1976f2] text-white px-8 py-3 rounded-full text-lg font-medium hover:bg-blue-600 transition font-montserrat"
        >
          Get A Call Back
        </button>
        <button 
          onClick={() => window.open('https://api.whatsapp.com/send/?phone=9599222655&text=Hello%2C+please+contact+me+regarding+https%3A%2F%2Fwww.medicallinkers.org%2F+%2C+Thank+you%21&type=phone_number&app_absent=0', '_blank')}
          className="border border-[#1976f2] text-[#1976f2] px-8 py-3 rounded-full text-lg font-medium hover:bg-blue-50 transition font-montserrat"
        >
          Contact Us
        </button>
      </div>
    </div>
  );
}

export default Home;

